// The detail of one review item: the protobuf field (type, comment), the
// AI's proposal with its confidence and reason, a newer proposal as a diff,
// and the form to accept, edit or reject it. Edited values go back as the
// item's edited proposal (state "edited"); they become classification.yaml
// entries when the review is written to the package.
import { useEffect, useState } from "preact/hooks";
import { Icon } from "./icons";
import { classesOf, hintsOf, isEntry, type Actions, type FieldInfo, type GeoFamily, type GeoSuggestion, type Vocabulary } from "./model";
import type { Hints, ReviewItem } from "../lib/types";
import { t, type Key } from "../i18n";

/** A review state or confidence word in the current language (unknown values as they are). */
function word(prefix: "kd.state." | "kd.conf.", v: string | null | undefined): string {
  if (!v) return "-";
  const s = t(`${prefix}${v}` as Key);
  return s === `${prefix}${v}` ? v : s;
}

const CLASSES = ["STATE", "TIMESERIES", "EVENT", "CONFIG", "COMMAND", "IGNORE"];
const ENTRY_CLASSES = ["STATE", "TIMESERIES", "EVENT", "CONFIG"];
const TYPES = ["number", "integer", "boolean", "string", "object", "enum", "flags", "timestamp", "duration", "geo"];

function qualText(q?: Record<string, string>): string {
  return Object.entries(q ?? {}).map(([k, v]) => `${k}=${v}`).join(", ");
}

function parseQual(s: string): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const part of s.split(",")) {
    const [k, ...v] = part.split("=");
    if (k.trim() && v.join("=").trim()) out[k.trim()] = v.join("=").trim();
  }
  return Object.keys(out).length ? out : undefined;
}

function Field({ label, children, hint }: { label: string; children: preact.ComponentChildren; hint?: string }) {
  return (
    <label class="flex flex-col gap-1">
      <span class="text-[11.5px] font-semibold text-ink2">{label}{hint && <span class="ml-1.5 font-normal text-dim">{hint}</span>}</span>
      {children}
    </label>
  );
}

/** CONFIG key parameter classes (store engine 0.17.0, devtype.KeyParamClasses). */
const PARAM_CLASSES = ["C0", "C1", "C2", "C3"] as const;

export function KeyDetail({ item, actions, writer, onClose, logicTypes, geoFamilies, paramClass }:
  { item: ReviewItem; actions: Actions; writer: boolean; onClose: () => void; logicTypes?: boolean; geoFamilies?: GeoFamily[]; paramClass?: boolean }) {
  const [info, setInfo] = useState<FieldInfo | null>(null);
  const [vocab, setVocab] = useState<Vocabulary | null>(null);
  const [h, setH] = useState<Hints>(hintsOf(item));
  const [qual, setQual] = useState(qualText(hintsOf(item).qualifiers));
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const entry = isEntry(item); // scenario C: a catalogue entry
  const classKey = entry ? "classes" : "class";
  const classes = classesOf(h);

  useEffect(() => {
    const hints = hintsOf(item);
    setH(hints);
    setQual(qualText(hints.qualifiers));
    setErr("");
    setInfo(null);
    if (item.proposal.field && !isEntry(item)) actions.fieldInfo(item.proposal.field).then(setInfo).catch(() => setInfo(null));
  }, [item.id, item.state]);
  useEffect(() => {
    actions.vocabulary().then(setVocab).catch(() => setVocab(null));
  }, []);

  const set = (k: keyof Hints, v: unknown) => setH((x) => ({ ...x, [k]: v === "" ? undefined : v }));
  const setAI = (k: "meaning" | "caveats" | "relates_to", v: string) =>
    setH((x) => {
      const ai = { ...(x.ai ?? {}) };
      if (k === "relates_to") ai.relates_to = v.split(",").map((s) => s.trim()).filter(Boolean);
      else ai[k] = v || undefined;
      return { ...x, ai };
    });
  const act = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    setErr("");
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  const save = () => act("edit", () => actions.edit(item.id, { ...item.proposal, hints: { ...h, qualifiers: parseQual(qual) } }));
  const role = vocab?.roles.find((r) => r.name === h.role);
  const comment = [info?.leading_comment, info?.trailing_comment].filter(Boolean).join(" ").trim();
  const ro = !writer;

  if (item.proposal.commands) return <CommandsDetail item={item} actions={actions} writer={writer} onClose={onClose} />;
  return (
    <aside aria-label={t("kd.aria")} class="flex w-[440px] flex-none flex-col border-l border-line2 bg-side">
      <div class="flex items-start gap-2 border-b border-line px-4 py-3">
        <div class="min-w-0 flex-1">
          <div class="truncate font-mono text-[13px] text-ink">{item.key_path}</div>
          <div class="truncate font-mono text-[11.5px] text-dim">{item.proposal.field ?? item.proposal.message}</div>
        </div>
        <button type="button" class="btn btn-ghost h-7 w-7 justify-center px-0" aria-label={t("kd.close")} onClick={onClose}><Icon name="x" size={14} /></button>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {info && (
          <section class="rounded-lg border border-line bg-bar px-3 py-2.5">
            <div class="font-mono text-[12px] text-accent2">{info.cardinality} {info.type}{info.type_name ? ` ${info.type_name}` : ""} {info.name} = {info.number}</div>
            {comment && <div class="mt-1 font-mono text-[12px] text-muted">// {comment}</div>}
          </section>
        )}
        <section class="rounded-lg border border-line px-3 py-2.5 text-[12.5px]">
          <div class="flex items-center gap-2">
            <span class="font-semibold">{t("kd.proposal")}</span>
            <span class="text-muted">{t("kd.confidence", { level: word("kd.conf.", item.confidence) })}</span>
            <span class="flex-1" />
            <span class="pill bg-hover text-ink2">{word("kd.state.", item.state)}</span>
          </div>
          <p class="mb-0 mt-1 text-muted">{item.reason || t("kd.noreason")}</p>
        </section>
        {item.pending_proposal && (
          <section class="rounded-lg border border-violet/40 bg-violet-soft/40 px-3 py-2.5 text-[12.5px]">
            <div class="font-semibold text-violet">{t("kd.pending")}</div>
            <pre class="mb-2 mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-[11.5px] text-ink2">{JSON.stringify(item.pending_proposal.hints, null, 1)}</pre>
            {item.state === "edited" && <p class="mb-2 mt-0 text-warn">{t("kd.pending.edited")}</p>}
            {writer && <button type="button" class="btn" disabled={!!busy} onClick={() => act("adopt", () => actions.decide(item.id, "adopt"))}>{t("kd.adopt")}</button>}
          </section>
        )}
        <p class="m-0 text-[12px] text-dim">{t("kd.hintsref").split("{link}")[0]}<a href="/help/hints" target="_blank">{t("kd.hintsref.link")}</a>{t("kd.hintsref").split("{link}")[1]}</p>
        <fieldset class="m-0 flex flex-col gap-3 border-0 p-0" disabled={ro}>
          <Field label={t("kd.class")}>
            <div class="flex flex-wrap gap-1.5">
              {(entry ? ENTRY_CLASSES : CLASSES).map((c) => {
                const on = classes.includes(c);
                return (
                  <button type="button" aria-pressed={on} onClick={() => set(classKey, on ? classes.filter((x) => x !== c) : [...classes, c])}
                    class={`pill h-6 px-2.5 ${on ? "bg-sel text-ink" : "border border-line2 text-muted"}`}>{c.toLowerCase()}</button>
                );
              })}
            </div>
          </Field>
          {entry && (
            <Field label={t("kd.type")}>
              <select class="field font-mono" value={(h.type as string) ?? ""} onChange={(e) => set("type", (e.target as HTMLSelectElement).value)}>
                {TYPES.map((ty) => <option value={ty}>{ty}</option>)}
              </select>
            </Field>
          )}
          <div class="grid grid-cols-2 gap-3">
            <Field label={t("kd.unit")} hint="UCUM">
              <input class="field font-mono" list="units" value={h.unit ?? ""} onInput={(e) => set("unit", (e.target as HTMLInputElement).value)} />
            </Field>
            <Field label={t("kd.nodata")}>
              <input class="field font-mono" value={h.null_value ?? ""} placeholder={t("kd.eg", { value: "0" })} onInput={(e) => set("null_value", (e.target as HTMLInputElement).value)} />
            </Field>
          </div>
          <div class="grid grid-cols-3 gap-3">
            {(["min", "max", "decimals"] as const).map((k) => (
              <Field label={k === "min" ? t("kd.min") : k === "max" ? t("kd.max") : t("kd.decimals")} hint={k === "decimals" ? t("kd.decimals.hint") : t("kd.minmax.hint")}>
                <input class="field font-mono" inputMode="decimal" value={h[k] === undefined || h[k] === null ? "" : String(h[k])}
                  onInput={(e) => {
                    const v = (e.target as HTMLInputElement).value.trim();
                    const n = Number(v);
                    set(k, v === "" || Number.isNaN(n) ? undefined : k === "decimals" ? Math.round(n) : n);
                  }} />
              </Field>
            ))}
          </div>
          <datalist id="units">{vocab?.kinds.flatMap((k) => [k.default, ...k.examples]).map((u) => <option value={u} />)}</datalist>
          <Field label={t("kd.role")} hint={role ? `${role.kind ?? t("kd.nokind")} · ${role.value_types.join("/")}` : undefined}>
            <select class="field font-mono" value={h.role ?? ""} onChange={(e) => set("role", (e.target as HTMLSelectElement).value)}>
              <option value="">{t("kd.norole")}</option>
              {(vocab?.roles ?? []).filter((r) => !r.deprecated).map((r) => <option value={r.name}>{r.name} - {r.label}</option>)}
              {h.role && !vocab?.roles.some((r) => r.name === h.role) && <option value={h.role}>{h.role}</option>}
            </select>
          </Field>
          <Field label={t("kd.qualifiers")} hint={t("kd.qualifiers.hint")}>
            <input class="field font-mono" value={qual} placeholder="of=battery" onInput={(e) => setQual((e.target as HTMLInputElement).value)} />
          </Field>
          <Field label={t("kd.label")}>
            <input class="field" value={h.label ?? ""} onInput={(e) => set("label", (e.target as HTMLInputElement).value)} />
          </Field>
          <Field label={t("kd.description")}>
            <textarea class="field h-auto py-1.5" rows={2} value={h.description ?? ""} onInput={(e) => set("description", (e.target as HTMLTextAreaElement).value)} />
          </Field>
          <Field label={t("kd.meaning")} hint={t("kd.meaning.hint")}>
            <textarea class="field h-auto py-1.5" rows={3} maxLength={300} value={h.ai?.meaning ?? ""} onInput={(e) => setAI("meaning", (e.target as HTMLTextAreaElement).value)} />
          </Field>
          <Field label={t("kd.relates")} hint={t("kd.relates.hint")}>
            <input class="field font-mono" value={(h.ai?.relates_to ?? []).join(", ")} onInput={(e) => setAI("relates_to", (e.target as HTMLInputElement).value)} />
          </Field>
          <Field label={t("kd.caveats")}>
            <textarea class="field h-auto py-1.5" rows={2} value={h.ai?.caveats ?? ""} onInput={(e) => setAI("caveats", (e.target as HTMLTextAreaElement).value)} />
          </Field>
          {/* geo meaning tag of a protobuf field (geo tags switch, GIOTHUB-64) */}
          {geoFamilies && geoFamilies.length > 0 && !entry && item.proposal.field && (
            <GeoFields field={item.proposal.field} h={h} set={set} families={geoFamilies} actions={actions} />
          )}
          {/* parameter class of a CONFIG key (engine 0.17.0, GIOTHUB-118) */}
          {paramClass && classes.includes("CONFIG") && (
            <Field label={t("kd.param")} hint={t("kd.param.hint")}>
              <select class="field font-mono" value={h.param_class ?? ""} onChange={(e) => set("param_class", (e.target as HTMLSelectElement).value || undefined)}>
                <option value="">{t("kd.param.none")}</option>
                {PARAM_CLASSES.map((c) => <option value={c}>{c} - {t(`kd.param.${c}`)}</option>)}
              </select>
            </Field>
          )}
          {/* engine 0.9.0 flags of CONFIG keys (logic types switch, GIOTHUB-31) */}
          {logicTypes && classes.includes("CONFIG") && (
            <>
              <label class="flex items-start gap-2.5 text-[13px]">
                <input type="checkbox" class="mt-1" checked={h.onboarding === "required"} onChange={() => set("onboarding", h.onboarding === "required" ? undefined : "required")} />
                <span>{t("kd.onboarding")}
                  <span class="block text-[12px] text-muted">{t("kd.onboarding.hint")}</span></span>
              </label>
              <Field label={t("kd.confirm")} hint={t("kd.confirm.hint")}>
                <textarea class="field h-auto py-1.5" rows={2} maxLength={300} value={h.confirm ?? ""} placeholder={t("kd.confirm.ph")}
                  onInput={(e) => set("confirm", (e.target as HTMLTextAreaElement).value)} />
              </Field>
            </>
          )}
        </fieldset>
        {err && <p class="m-0 text-[13px] text-crit">{err}</p>}
      </div>
      {writer && (
        <div class="flex flex-none flex-wrap gap-2 border-t border-line px-4 py-3">
          <button type="button" class="btn text-crit" disabled={!!busy} onClick={() => act("reject", () => actions.decide(item.id, "reject"))}>{t("kd.reject")}</button>
          <span class="flex-1" />
          <button type="button" class="btn" disabled={!!busy} onClick={save}>{busy === "edit" ? t("kd.saving") : t("kd.saveedit")}</button>
          <button type="button" class="btn btn-pri" disabled={!!busy} onClick={() => act("accept", () => actions.decide(item.id, "accept"))}>{t("kd.accept")}</button>
        </div>
      )}
    </aside>
  );
}

// GeoFields: the field's geo meaning (GIOTHUB-64). The suggestions come from
// the field's name (English, German, Italian, French) and unit; nothing is
// applied until the author picks it.
function GeoFields({ field, h, set, families, actions }: {
  field: string; h: Hints; set: (k: keyof Hints, v: unknown) => void; families: GeoFamily[]; actions: Actions;
}) {
  const [sug, setSug] = useState<GeoSuggestion[]>([]);
  useEffect(() => {
    actions.geoSuggest(field, h.unit ?? "").then(setSug).catch(() => setSug([]));
  }, [field, h.unit]);
  const tag = h.geo ?? "";
  const part = families.flatMap((f) => f.parts).find((p) => p.tag === tag);
  const params = h.geo_params ?? {};
  const setParam = (k: "rat_map" | "gnb_bits" | "alt_ref", v: unknown) => {
    const next = { ...params, [k]: v === "" || v === undefined ? undefined : v };
    set("geo_params", Object.values(next).some((x) => x !== undefined) ? next : undefined);
  };
  const ratText = Object.entries(params.rat_map ?? {}).map(([k, v]) => `${k}=${v}`).join(", ");
  return (
    <div class="flex flex-col gap-2 rounded-lg border border-line px-3 py-2.5">
      <Field label={t("kd.geo")} hint={part?.unit ? t("kd.geo.takes", { unit: part.unit }) : t("kd.geo.hint")}>
        <select class="field font-mono" value={tag}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            set("geo", v);
            if (!v) { set("geo_group", undefined); set("geo_params", undefined); }
          }}>
          <option value="">{t("kd.geo.none")}</option>
          {families.map((f) => (
            <optgroup label={f.label}>{f.parts.map((p) => <option value={p.tag}>{p.tag} - {p.label}</option>)}</optgroup>
          ))}
          {tag && !part && <option value={tag}>{tag}</option>}
        </select>
      </Field>
      {sug.filter((s) => s.tag !== tag).length > 0 && (
        <div class="flex flex-col gap-1 text-[12px]">
          <span class="text-dim">{t("kd.geo.suggested")}</span>
          {sug.filter((s) => s.tag !== tag).map((s) => (
            <div class="flex items-center gap-2">
              <button type="button" class="btn h-6 px-2 text-[12px]" onClick={() => set("geo", s.tag)}>{t("kd.geo.use", { tag: s.tag })}</button>
              <span class="truncate text-muted">{s.label} · {s.reason}</span>
            </div>
          ))}
        </div>
      )}
      {tag.startsWith("gnss.") && (
        <Field label={t("kd.geo.group")} hint={t("kd.geo.group.hint")}>
          <input class="field font-mono" value={h.geo_group ?? ""} placeholder={t("kd.eg", { value: "last_good" })} onInput={(e) => set("geo_group", (e.target as HTMLInputElement).value)} />
        </Field>
      )}
      {tag === "gnss.alt" && (
        <Field label={t("kd.geo.altref")}>
          <select class="field font-mono" value={params.alt_ref ?? ""} onChange={(e) => setParam("alt_ref", (e.target as HTMLSelectElement).value)}>
            <option value="">{t("kd.geo.altref.default")}</option>
            <option value="ellipsoid">ellipsoid</option>
          </select>
        </Field>
      )}
      {tag === "cell.gnb_id" && (
        <Field label={t("kd.geo.gnbbits")} hint={t("kd.geo.gnbbits.hint")}>
          <input class="field font-mono" inputMode="numeric" value={params.gnb_bits ?? ""}
            onInput={(e) => { const n = Number((e.target as HTMLInputElement).value); setParam("gnb_bits", Number.isFinite(n) && n > 0 ? Math.round(n) : undefined); }} />
        </Field>
      )}
      {tag === "cell.rat" && (
        <Field label={t("kd.geo.rat")} hint={t("kd.geo.rat.hint")}>
          <input class="field font-mono" value={ratText} placeholder="RAT_LTE_M=lte, RAT_NB_IOT=nbiot"
            onInput={(e) => {
              const m: Record<string, string> = {};
              for (const kv of (e.target as HTMLInputElement).value.split(",")) {
                const [k, v] = kv.split("=").map((x) => x?.trim());
                if (k && v) m[k] = v;
              }
              setParam("rat_map", Object.keys(m).length ? m : undefined);
            }} />
        </Field>
      )}
    </div>
  );
}

interface CommandItem { label?: string; description?: string; confirm?: string; hidden?: boolean; args?: { name: string; label?: string; unit?: string; min?: number; max?: number }[] }

/** A commands proposal (the classification's commands section): read, accept or reject; edits go into classification.yaml after it is written. */
function CommandsDetail({ item, actions, writer, onClose }: { item: ReviewItem; actions: Actions; writer: boolean; onClose: () => void }) {
  const h = hintsOf(item) as { enum?: string; items?: Record<string, CommandItem> };
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const act = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    setErr("");
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  };
  return (
    <aside aria-label={t("kd.commands")} class="flex w-[440px] flex-none flex-col border-l border-line2 bg-side">
      <div class="flex items-start gap-2 border-b border-line px-4 py-3">
        <div class="min-w-0 flex-1">
          <div class="truncate text-[13px] font-semibold text-ink">{t("kd.commands")}</div>
          <div class="truncate font-mono text-[11.5px] text-dim">{h.enum}</div>
        </div>
        <button type="button" class="btn btn-ghost h-7 w-7 justify-center px-0" aria-label={t("kd.close")} onClick={onClose}><Icon name="x" size={14} /></button>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        <section class="rounded-lg border border-line px-3 py-2.5 text-[12.5px]">
          <div class="flex items-center gap-2">
            <span class="font-semibold">{t("kd.proposal")}</span>
            <span class="text-muted">{t("kd.confidence", { level: word("kd.conf.", item.confidence) })}</span>
            <span class="flex-1" />
            <span class="pill bg-hover text-ink2">{word("kd.state.", item.state)}</span>
          </div>
          <p class="mb-0 mt-1 text-muted">{item.reason || t("kd.noreason")}</p>
        </section>
        <p class="m-0 text-[12.5px] text-muted">{t("kd.commands.intro")}</p>
        {Object.entries(h.items ?? {}).map(([name, c]) => (
          <section class="rounded-lg border border-line px-3 py-2.5 text-[12.5px]">
            <div class="flex items-center gap-2">
              <span class="font-semibold">{c.label ?? name}</span>
              {c.hidden && <span class="pill h-4 bg-hover px-1.5 text-dim">{t("kd.commands.hidden")}</span>}
              <span class="flex-1" />
              <span class="font-mono text-[11px] text-dim">{name}</span>
            </div>
            {c.description && <p class="mb-0 mt-1 text-muted">{c.description}</p>}
            {c.confirm && <p class="mb-0 mt-1 text-warn">{t("kd.commands.asks", { question: c.confirm })}</p>}
            {(c.args ?? []).map((a) => (
              <p class="mb-0 mt-1 font-mono text-[11.5px] text-ink2">{a.name}{a.label ? ` - ${a.label}` : ""}{a.unit ? ` (${a.unit})` : ""}
                {a.min !== undefined || a.max !== undefined ? ` ${a.min ?? ""}..${a.max ?? ""}` : ""}</p>
            ))}
          </section>
        ))}
        {err && <p class="m-0 text-[13px] text-crit">{err}</p>}
      </div>
      {writer && (
        <div class="flex gap-2 border-t border-line px-4 py-3">
          <button type="button" class="btn text-crit" disabled={!!busy} onClick={() => act("reject", () => actions.decide(item.id, "reject"))}>{t("kd.reject")}</button>
          <span class="flex-1" />
          <button type="button" class="btn btn-pri" disabled={!!busy} onClick={() => act("accept", () => actions.decide(item.id, "accept"))}>{t("kd.accept")}</button>
        </div>
      )}
    </aside>
  );
}
